import { computed, makeAutoObservable, observable } from 'mobx'
import { schema } from 'prose-core'
import React, { useContext } from 'react'

import { CollabItemType, CommentItem } from 'stores/DocStore.types'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { type RootStore, useRootStore } from 'stores/RootStore'
import { MobileToolbarStore, useMobileToolbar } from '../stores/MobileToolbarStore'

export enum CollaborationDialogState {
  NewComment,
  AllComments,
  SelectedComment,
}

export class CollaborationDialogStore {
  fullScreen: boolean = false
  newComment: boolean = false
  viewAll: boolean = false
  showFilters: boolean = false
  intentionallyCloseFilters: boolean = false
  constructor(
    private readonly rootStore: RootStore,
    private readonly almanacState: AlmanacEditorState | null,
    mobileStore: MobileToolbarStore | null,
  ) {
    makeAutoObservable(this, {
      viewAll: observable,
      fullScreen: observable,
      showFilters: observable,
      selectedComment: computed,
      allCommentsLength: computed,
      filteredCommentsLength: computed,
      currentState: computed,
    })
    mobileStore?.onNewComment(() => this.openNewComment())
  }
  get selectedComment() {
    return this.rootStore.docStore?.comments.find((comment) => comment.id === this.rootStore.comments.selected?.id)
  }
  get isNodeCommentable() {
    return this.almanacState?.isSelectedNodeCommentable
  }
  get currentSelectionHasComments() {
    return this.almanacState?.activeMarks.has(schema.marks.comment)
  }
  get filteredCommentsLength() {
    return count(
      this.rootStore.docStore?.orderedFilteredCollabItems,
      (e): e is CommentItem => e.type === CollabItemType.Comment,
    )
  }
  get allCommentsLength() {
    return count(this.rootStore.docStore?.collabItems, (e): e is CommentItem => e.type === CollabItemType.Comment)
  }
  get currentState(): CollaborationDialogState {
    if (this.newComment && this.isNodeCommentable) {
      return CollaborationDialogState.NewComment
    }
    if ((this.selectedComment || this.currentSelectionHasComments) && !this.viewAll) {
      return CollaborationDialogState.SelectedComment
    }
    return CollaborationDialogState.AllComments
  }
  toggleViewAll = () => {
    // When clicking view all, show some basic filters by default, people can close it if they don't care
    if (!this.intentionallyCloseFilters && !this.viewAll) {
      this.showFilters = true
      // TODO: We want to do this but it doesn't feel right right now
      // this.fullScreen = true;
    } else {
      this.intentionallyCloseFilters = true
    }
    this.viewAll = !this.viewAll
  }
  toggleFullscreen = () => {
    this.fullScreen = !this.fullScreen
  }
  toggleFilters = () => {
    this.showFilters = !this.showFilters
  }
  openNewComment = () => {
    this.newComment = true
  }
  closeNewComment = () => {
    this.newComment = false
  }
}

export const CollaborationDialogContext = React.createContext<CollaborationDialogStore | null>(null)

export const CollaborationDialogProvider = ({ children }: { children: React.ReactNode }) => {
  const almanacState = useContext(AlmanacEditorStateContext)
  const rootStore = useRootStore()
  const mobileStore = useMobileToolbar()
  const store = React.useMemo(
    () => new CollaborationDialogStore(rootStore, almanacState, mobileStore),
    [rootStore, almanacState, mobileStore],
  )

  return <CollaborationDialogContext.Provider value={store}>{children}</CollaborationDialogContext.Provider>
}

export const useCollaborationDialog = () => {
  const context = React.useContext(CollaborationDialogContext)
  if (context == null) {
    throw new Error('useCollaborationDialog must be within CollaborationDialogContext')
  }

  return context
}

function count<T>(arr: T[] = [], fn: (val: T) => boolean): number {
  let count = 0
  for (let i = 0; i < arr.length; i++) {
    if (fn(arr[i])) count++
  }
  return count
}
